import { globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { debounce, hide, parseToBoolean, show } from '@shared/easyfy.utils';
import searchService from '@webServices/search.service';
import { Backdrop } from '@webModules/backdrop';

namespace Meds.Modules {
  @invoke
  export class Search {
    static Instance: Search;

    private searchInput: HTMLInputElement;

    private searchResults: HTMLElement;

    private autoCompleteInitView: string;

    private autoCompleteInitViewListName: string;

    private searchBarElement: HTMLElement;

    private searchIcon: HTMLElement;

    private pendingSearch: string;

    private minQueryLength: number;

    private initialized: boolean;

    constructor() {
      Search.Instance = this;

      this.minQueryLength = 2;
    }

    onInit() {
      globalEvent({
        type: 'focusin keyup',
        event: {
          action: 'search-input',
          callback: debounce(Search.onSearch, 200)
        }
      });

      globalEvent({
        type: 'click',
        event: {
          event: 'search-close',
          callback: Search.onCloseSearch
        }
      });

      globalEvent({
        type: 'click',
        event: {
          action: 'search-suggestion',
          callback: Search.onSearchSuggestion
        }
      });

      globalEvent({
        type: 'click',
        event: {
          callback: (e) => {
            if ((e.target as HTMLElement).id === 'backdrop') {
              Search.Instance.searchClear();
            }
          }
        }
      });
    }

    /** Fills the search input with the selected suggestion value and refocuses it. */
    static onSearchSuggestion = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const element: HTMLElement = e.target as HTMLElement;
      const newValue = element.attributes['data-value']?.value;

      Search.Instance.searchInput.value = newValue;
      Search.Instance.searchInput.focus();

      return false;
    };

    /** Handles focusin and keyup events on the search input; fetches and renders autocomplete results. */
    static onSearch = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.type === 'focusin') {
        Search.initNewSearchInstance(e.target as HTMLInputElement);
          Search.Instance.initialized = true;

      }

      if (e.type !== 'focusin' && e.type !== 'keyup') {
        return false;
      }

      Backdrop.show('backdrop-content');

      show(Search.Instance.searchIcon);
      const value: string = Search.Instance.searchInput.value.trim() ?? '';

      if (Search.Instance.pendingSearch !== value || Search.Instance.pendingSearch === '') {
        // prevents multiple requests from different events for same query
        Search.Instance.pendingSearch = value;

        const targetAttrs = e.target.attributes;
        const token = targetAttrs['data-token']?.value;

        const searchData = {
          IncludeCategories: parseToBoolean(
            targetAttrs['data-include-categories']?.value
          ),
          includeBrands: parseToBoolean(
            targetAttrs['data-include-brands']?.value
          ),
          includePages: parseToBoolean(
            targetAttrs['data-include-pages']?.value
          ),
          includeCampaigns: parseToBoolean(
            targetAttrs['data-include-campaigns']?.value
          ),
          searchTerm: Search.Instance.pendingSearch
        };
        
        if (
          Search.Instance.pendingSearch &&
          Search.Instance.pendingSearch.length >=
          Search.Instance.minQueryLength
        ) {
          searchService.Search(searchData, token).then((response) => {
            if (Search.Instance.pendingSearch === response.data.query) {
              Search.Instance.pendingSearch = null;
            }

            if (response.data.success) {
              // ignore result if input was emptied during fetch of data
              if (Search.Instance.searchInput.value.trim()) {
                Search.Instance.searchResults.innerHTML = response.data.partial;
                Search.Instance.searchResults.dataset.listName = response.data.itemListName;

                show(Search.Instance.searchResults);
              }
            }
          });
        } else if (!Search.Instance.initialized) {
          Search.Instance.searchClear();
        } else if (Search.Instance.pendingSearch != null && Search.Instance.pendingSearch.length < Search.Instance.minQueryLength) {

          if (!Search.Instance.autoCompleteInitView) {
            // only trigger the get from server once
            await searchService.GetAutoCompleteInitView().then((response) => {
              if (response.data.success && response.data.partial !== null) {
                Search.Instance.autoCompleteInitView = response.data.partial;
                Search.Instance.autoCompleteInitViewListName = response.data.itemListName;
              }
            });
          }

          if (Search.Instance.autoCompleteInitView) {
            Search.Instance.searchResults.innerHTML = Search.Instance.autoCompleteInitView;
            Search.Instance.searchResults.dataset.listName = Search.Instance.autoCompleteInitViewListName;
            show(Search.Instance.searchResults);
          }
        }
      }

      return false;
    };

    /** Resolves and caches the search bar, results container, and close icon relative to the given input. */
    static initNewSearchInstance = async (
      searchInputElement: HTMLInputElement
    ) => {
      Search.Instance.searchInput = searchInputElement;
      Search.Instance.searchBarElement = searchInputElement.closest(
        '[data-container="search-bar"]'
      );
      Search.Instance.searchResults =
        Search.Instance.searchBarElement.querySelector(
          '[data-container="search-result"]'
        ) as HTMLElement;

      Search.Instance.searchIcon =
        Search.Instance.searchBarElement.querySelector(
          '[data-event="search-close"]'
        ) as HTMLElement;
    };

    /** Clears the search state when the close button is clicked. */
    static onCloseSearch(e) {
      e.preventDefault();
      e.stopPropagation();

      Search.Instance.searchClear();

      return false;
    }

    /** Clears the search input, hides results and the close icon, and hides the backdrop. */
    searchClear() {
      if (!Search.Instance.searchInput) {
        return;
      }

      Search.Instance.hideSearchResult(null);
      Backdrop.hide();
      Search.Instance.searchInput.value = '';

      if (Search.Instance.searchIcon != null) {
        hide(Search.Instance.searchIcon);
      }
    }

    /** Hides search results when focus moves outside the search bar. */
    searchBarInFocus(e) {
      if (e.type === 'focusin') {
        if (
          !Search.Instance.isEventTargetInsideSearch(e) &&
          Search.Instance.searchResults != null
        ) {
          hide(Search.Instance.searchResults);
        }
      }
    }

    /** Hides the search results container, optionally only when the event target is outside the search bar. */
    hideSearchResult(e: any) {
      if (e?.target) {
        if (!Search.Instance.isEventTargetInsideSearch(e)) {
          hide(Search.Instance.searchResults);
        }
      } else {
        hide(Search.Instance.searchResults);
      }
    }

    /** Shows the search results container, optionally only when the event target is outside the search bar. */
    showSearchResult(e: any) {
      if (e?.target) {
        if (!Search.Instance.isEventTargetInsideSearch(e)) {
          show(Search.Instance.searchResults);
        }
      } else {
        show(Search.Instance.searchResults);
      }
    }

    /** Returns true if the event target is inside the search bar element. */
    isEventTargetInsideSearch(e: any) {
      if (this.searchBarElement == null) {
        return false;
      }
      return this.searchBarElement.contains(e.target);
    }
  }
}

export default Meds.Modules;

